import { useEffect, useImperativeHandle, useMemo, useState } from "react";
import * as React from "react";
import { match, P } from "ts-pattern";
import AccordionTable from "@/components/admin/displayGroup/AccordionTable";
import { CategoryData } from "@/components/admin/displayGroup/AccordionTable/RenderNode";
import { Input } from "@/components/atomic/Input";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { LandingPath } from "@/models/categoryTree";
import { DisplayGroup } from "@/models/displayGroup";

interface Props {
  submit: (name: string, paths: string[]) => Promise<void>;
  landingPaths: LandingPath[];
  displayGroup?: DisplayGroup;
  initialName?: string;
}

interface FormData {
  name: string;
  selectedPaths: string[];
}

// 親コンポーネントにExposeする関数の型定義
export interface DisplayGroupFormRef {
  triggerSubmit: () => void;
}

// AccordionTableに渡すデータを構築するための関数
const buildTableData = (
  landingPaths: LandingPath[],
  displayGroups: DisplayGroup[],
): Record<string, CategoryData> => {
  const data: Record<string, CategoryData> = {};

  // 表示層のカテゴリパスがキー、そのカテゴリが紐づくグループとバージョンが値のデータを作成
  landingPaths.forEach((landingPath) => {
    data[landingPath.path] = {
      groups: displayGroups
        .filter(group => group.categoryPaths.includes(landingPath.path))
        .map(group => group.name), // カテゴリパスから表示グループ名を取得
      versions: landingPath.containedVersions,
    };
  });

  return data;
};

const DisplayGroupForm = React.forwardRef<DisplayGroupFormRef, Props>(({
  submit,
  landingPaths,
  displayGroup,
  initialName,
}, ref) => {
  const { categoryMaxLevel } = useWorkspaces();
  const { displayGroups } = useDisplayGroups();
  const [formData, setFormData] = useState<FormData>({
    name: initialName || "",
    selectedPaths: [],
  });
  const [nameError, setNameError] = useState<string | null>(null);

  const accordionTableData = useMemo(
    () => buildTableData(landingPaths, displayGroups ?? []),
    [landingPaths, displayGroups],
  );

  const validateName = (value: string) =>
    match(value.length)
      .with(0, () => "表示グループを入力してください。")
      .with(P.when((v) => v > 50), () => "50文字以内で入力してください。")
      .otherwise(() => null);

  useEffect(() => {
    setFormData({
      name: displayGroup?.name ?? initialName ?? "",
      selectedPaths: displayGroup?.categoryPaths ?? [],
    });
  }, [displayGroup?.id, displayGroup?.name, displayGroup?.categoryPaths, initialName]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, name: value }));
    setNameError(validateName(value));
  };

  const handlePathSelection = (selectedPaths: string[]) => {
    // LandingLevelのパスに絞る
    const landingLevelPaths = selectedPaths.filter(path =>
      path.split("/").filter(Boolean).length === categoryMaxLevel - 1
    );
    setFormData(prev => ({ ...prev, selectedPaths: landingLevelPaths }));
  };

  const handleClickSubmit = async () => {
    const error = validateName(formData.name);
    if (error) {
      setNameError(error);
      return;
    }

    await submit(formData.name, formData.selectedPaths);
  };

  // 親コンポーネントにExposeする関数を定義
  useImperativeHandle(ref, () => ({
    triggerSubmit: handleClickSubmit,
  }));

  return (
    <div className="relative mt-5">
      <form noValidate>
        <div>
          {/* 表示グループ名入力 */}
          <div className={cn("flex align-center", nameError ? "mb-3" : "mb-8")}>
            <label className="text-lg font-medium leading-[42px] mr-4" htmlFor="display-group-name">
              表示グループ名<span className="text-red-500">*</span> :
            </label>
            <div className="flex-1">
              <Input
                id="display-group-name"
                data-testid="display-group-name-input"
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="グループ名が入ります。グループ名が入ります。"
                className={`w-full max-w-md px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  nameError ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
            </div>
          </div>

          {/* カテゴリ選択 */}
          <div data-testid="category-accordion-table">
            <AccordionTable
              data={accordionTableData}
              emptyMessage="まだカテゴリが作成されていません"
              selectedPaths={formData.selectedPaths}
              onSelectionChange={handlePathSelection}
            />
          </div>
        </div>
      </form>
    </div>
  );
});
DisplayGroupForm.displayName = "DisplayGroupForm";

export default DisplayGroupForm;
